import type {
  SignalAdminDraft,
  SignalAdminRegistry,
  TargetPatch,
  TargetRecord,
} from "./types";

/**
 * Target Registry as the draft would leave it. Mirrors target_registry() /
 * target_available() / target_accepts_new_mappings() in the backend
 * (services/signal_admin_config_service.py): the draft's patch is laid
 * over the applied record, then the derived fields are recomputed.
 */

export const newTargetRecord = (id: string): TargetRecord => ({
  id,
  label: "",
  description: "",
  surface_type: "page",
  parent_page: "",
  route: "",
  route_label: "",
  legacy_route: "",
  kind: "path",
  provenance: "Admin-authored target",
  personas: [],
  permission: "Assigned account team",
  required_context: "account_id",
  review_status: "confirmed",
  legacy_active: false,
  enabled: true,
  fallback_target: "",
  seeded: false,
  available: true,
  accepts_new_mappings: true,
  mappings: [],
});

const finish = (record: TargetRecord): TargetRecord => {
  const legacyActive = record.seeded && record.review_status === "needs_review";
  const available =
    record.review_status !== "rejected" &&
    (record.review_status === "confirmed" || legacyActive) &&
    record.enabled;
  return {
    ...record,
    route_label: record.kind === "path" ? record.label : record.route_label,
    legacy_active: legacyActive,
    available,
    accepts_new_mappings:
      record.review_status === "confirmed" && !!record.kind && available,
  };
};

/** The draft's CTA target for a definition (override, else the
 * catalogue's). */
export const draftMappedTarget = (
  registry: SignalAdminRegistry,
  draft: SignalAdminDraft,
  definitionId: string
): string => {
  const definition = registry.definitions.find(d => d.id === definitionId);
  return draft.ctas[definitionId]?.target || definition?.target || "";
};

export const draftTargets = (
  registry: SignalAdminRegistry,
  draft: SignalAdminDraft
): TargetRecord[] => {
  const byId = new Map(registry.target_registry.map(t => [t.id, t]));
  Object.entries(draft.targets).forEach(([id, patch]) => {
    if (patch === null)
      byId.delete(id); // staged for removal
    else if (!byId.has(id)) byId.set(id, newTargetRecord(id));
  });
  const canonical = registry.definitions.filter(
    d => d.definition_status === "canonical"
  );
  return Array.from(byId.values()).map(applied => {
    const patch: TargetPatch = draft.targets[applied.id] ?? {};
    const record = finish({ ...applied, ...patch } as TargetRecord);
    return {
      ...record,
      mappings: canonical
        .filter(d => draftMappedTarget(registry, draft, d.id) === record.id)
        .map(d => d.id),
    };
  });
};

/** Stage `patch` on a target, keeping whatever the draft already holds
 * for it. Rejecting also disables it, as the backend does. */
/** Stage removing an admin-authored target: null for one already applied,
 * or just drop an unapplied new one. */
export const stageTargetRemoval = (
  draft: SignalAdminDraft,
  id: string,
  applied: boolean
): SignalAdminDraft => {
  const targets = { ...draft.targets };
  if (applied) targets[id] = null;
  else delete targets[id];
  return { ...draft, targets };
};

export const stageTarget = (
  draft: SignalAdminDraft,
  id: string,
  patch: TargetPatch
): SignalAdminDraft => {
  const next: TargetPatch = { ...(draft.targets[id] ?? {}), ...patch };
  if (next.review_status === "rejected") next.enabled = false;
  return { ...draft, targets: { ...draft.targets, [id]: next } };
};

export const REVIEW_META: Record<
  TargetRecord["review_status"],
  { label: string; className: string }
> = {
  needs_review: {
    label: "Needs review",
    className: "bg-brand-tint text-brand-dark ring-brand/20",
  },
  confirmed: {
    label: "Confirmed",
    className: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  },
  rejected: {
    label: "Rejected",
    className: "bg-red-50 text-red-700 ring-red-600/20",
  },
};

export const SURFACE_LABEL: Record<TargetRecord["surface_type"], string> = {
  page: "Page",
  tab: "Tab",
  unclassified: "Unclassified",
};

/** Mirrors _valid_path() in the backend. */
export const validTargetPath = (route: string): boolean => {
  if (!route.startsWith("/") || route.startsWith("//")) return false;
  if (/\s/.test(route) || route.split("?")[0].includes(":")) return false;
  const placeholders = route.match(/\{[^}]*\}/g) ?? [];
  return placeholders.every(p => p === "{account_id}");
};

export const TARGET_ID_PATTERN = /^[a-z][a-z0-9_]{1,63}$/;
